import React, { useState, useEffect } from 'react';
import { offlineManager } from '../utils/offlineManager';
import LoadingSpinner from './LoadingSpinner';

interface OfflineIndicatorProps {
  className?: string;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ className = '' }) => {
  const [isOnline, setIsOnline] = useState(offlineManager.isConnected());
  const [queueLength, setQueueLength] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  useEffect(() => {
    // Listen for connectivity changes
    const unsubscribeConnectivity = offlineManager.onConnectivityChange((online) => {
      setIsOnline(online);
      if (online) {
        setIsSyncing(true);
        setLastSyncTime(new Date());
      }
    });

    // Listen for queue updates
    const unsubscribeQueue = offlineManager.onQueueUpdate((length) => {
      setQueueLength(length);
      if (length === 0 && isSyncing) {
        setIsSyncing(false);
      }
    });

    // Get initial queue status
    offlineManager.getQueueStatus().then(status => {
      setQueueLength(status.queueLength);
    });

    return () => {
      unsubscribeConnectivity();
      unsubscribeQueue();
    };
  }, [isSyncing]);

  const handleManualSync = async () => {
    if (isOnline && !isSyncing) {
      setIsSyncing(true);
      await offlineManager.syncOfflineQueue();
      setIsSyncing(false);
      setLastSyncTime(new Date());
    }
  };

  const getStatusIcon = () => {
    return null;
  };

  const getStatusText = () => {
    if (isSyncing) {
      return 'Syncing...';
    }
    
    if (!isOnline) {
      return 'Offline';
    }
    
    if (queueLength > 0) {
      return `${queueLength} pending`;
    }
    
    return 'Online';
  };

  const getStatusColor = () => {
    if (isSyncing) {
      return 'text-blue-600 bg-blue-50 border-blue-200';
    }
    
    if (!isOnline) {
      return 'text-red-600 bg-red-50 border-red-200';
    }
    
    if (queueLength > 0) {
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
    
    return 'text-green-600 bg-green-50 border-green-200';
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor()}`}>
        {getStatusIcon()}
        <span>{getStatusText()}</span>
      </div>
      
      {isOnline && queueLength > 0 && (
        <button
          onClick={handleManualSync}
          disabled={isSyncing}
          className="p-1 hover:bg-white/20 rounded-full transition-colors"
          title="Sync pending requests"
        >
          {isSyncing ? <LoadingSpinner size="xs" className="text-white" /> : <span className="text-white">↻</span>}
        </button>
      )}
      
      {lastSyncTime && (
        <span className="text-xs text-white/80">
          Last sync: {lastSyncTime.toLocaleTimeString()}
        </span>
      )}
    </div>
  );
};

interface OfflineBannerProps {
  className?: string;
}

export const OfflineBanner: React.FC<OfflineBannerProps> = ({ className = '' }) => {
  const [isOnline, setIsOnline] = useState(offlineManager.isConnected());
  const [queueLength, setQueueLength] = useState(0);

  useEffect(() => {
    const unsubscribeConnectivity = offlineManager.onConnectivityChange((online) => {
      setIsOnline(online);
    });

    const unsubscribeQueue = offlineManager.onQueueUpdate((length) => {
      setQueueLength(length);
    });

    offlineManager.getQueueStatus().then(status => {
      setQueueLength(status.queueLength);
    });

    return () => {
      unsubscribeConnectivity();
      unsubscribeQueue();
    };
  }, []);

  if (isOnline && queueLength === 0) {
    return null;
  }

  return (
    <div className={`bg-yellow-50 border-l-4 border-yellow-400 p-4 ${className}`}>
      <div className="flex items-center">
        <div className="flex-shrink-0">
        </div>
        <div className="ml-3">
          <p className="text-sm text-yellow-700">
            {!isOnline ? (
              <>
                <strong>You're offline.</strong> Your work is being saved locally and will sync when connectivity is restored.
              </>
            ) : (
              <>
                <strong>{queueLength} requests pending sync.</strong> Your offline work is being synchronized.
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

interface OfflineToastProps {
  message: string;
  type: 'success' | 'warning' | 'error';
  onClose: () => void;
}

export const OfflineToast: React.FC<OfflineToastProps> = ({ message, type, onClose }) => {
  const getIcon = () => {
    return null;
  };

  const getColorClass = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
    }
  };

  return (
    <div className={`fixed top-4 right-4 z-50 max-w-sm w-full bg-white border rounded shadow-lg ${getColorClass()}`}>
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-3 w-0 flex-1">
            <p className="text-sm font-medium">
              {message}
            </p>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              onClick={onClose}
              className="bg-white rounded inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <span className="sr-only">Close</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook for offline status
export const useOfflineStatus = () => {
  const [isOnline, setIsOnline] = useState(offlineManager.isConnected());
  const [queueLength, setQueueLength] = useState(0);

  useEffect(() => {
    const unsubscribeConnectivity = offlineManager.onConnectivityChange((online) => {
      setIsOnline(online);
    });

    const unsubscribeQueue = offlineManager.onQueueUpdate((length) => {
      setQueueLength(length);
    });

    offlineManager.getQueueStatus().then(status => {
      setQueueLength(status.queueLength);
    });

    return () => {
      unsubscribeConnectivity();
      unsubscribeQueue();
    };
  }, []);

  return {
    isOnline,
    queueLength,
    isOffline: !isOnline,
    hasPendingRequests: queueLength > 0
  };
};
